import { MockRoute } from '../mockBackend';
import { PreviewDataset, PreviewEmployee, Proficiency } from '../../preview.types';

// Mock responses for the Team page (People + Competencies tabs and their mount-time fan-out).
// These builders ARE the "backend": they compute the same derived figures (coverage, gaps,
// compliance, met/gap employees) the real API would, and match the EXACT backend DTO shapes so the
// real Team components parse them unchanged. System.Text.Json defaults → camelCase, enums as ints.
//
// Covers People, Competencies, Tasks and Training tabs + the profile drill-in's /Teams/employees/:id.
// (/Profile and the profile-dialog Training sub-tab endpoints live in ./profile.ts.)

const PROFICIENCY_VALUE: Record<Proficiency, number> = {
  developing: 1,
  competent: 2,
  expert: 3,
};

const ACTIVE = 2; // EmployeeStatus.Active

interface Coverage {
  employee: PreviewEmployee;
  jobTitle: string;
  /** Whether the employee is assigned to a job at all (jobless workforces ⇒ no requirements/gaps). */
  hasJob: boolean;
  mandatory: string[];
  required: string[];
  desirable: string[];
  /** mandatory ∪ required — the compliance set that drives gaps. Desirable is excluded. */
  mustHave: string[];
  heldIds: Set<string>;
  gaps: string[];
}

function coverages(dataset: PreviewDataset): Coverage[] {
  const jobs = new Map(dataset.jobs.map((j) => [j.id, j]));
  return dataset.employees.map((employee) => {
    const job = employee.jobId ? jobs.get(employee.jobId) : undefined;
    const mandatory = job?.mandatoryAttributeIds ?? [];
    const required = job?.requiredAttributeIds ?? [];
    const desirable = job?.desirableAttributeIds ?? [];
    const mustHave = [...mandatory, ...required];
    const heldIds = new Set(employee.attributes.map((a) => a.attributeDefinitionId));
    return {
      employee,
      jobTitle: job?.title ?? '',
      hasJob: !!job,
      mandatory,
      required,
      desirable,
      mustHave,
      heldIds,
      gaps: mustHave.filter((id) => !heldIds.has(id)),
    };
  });
}

// Met count within one bucket for one employee.
const metCount = (c: Coverage, ids: string[]) => ids.filter((id) => c.heldIds.has(id)).length;

// Team filtering is SERVER-SIDE in the real app: every tab serialises the filter model to query
// params (EmployeeIds/Jobs/Locations/AttributeDefinitionIds/IsCompliant/ExpiryDateFrom-To — see
// common/models/TeamFilterModel + the *Section components) and re-fetches. The mock IS the backend,
// so it must apply them or filters appear to do nothing. This narrows the employee POPULATION;
// AttributeDefinitionIds is handled per-tab (people = holders; competencies = which cards to show).
function filterPopulation(rows: Coverage[], params: URLSearchParams): Coverage[] {
  const employeeIds = params.getAll('EmployeeIds');
  const jobs = params.getAll('Jobs'); // job TITLES (see buildFilterOptions)
  const locations = params.getAll('Locations');
  const isCompliant = params.get('IsCompliant'); // 'true' | 'false' | null
  const expiryFrom = params.get('ExpiryDateFrom'); // ISO yyyy-MM-dd
  const expiryTo = params.get('ExpiryDateTo');
  // AttributeType is intentionally ignored — the fixture has only "required" competencies.

  return rows.filter((c) => {
    if (employeeIds.length && !employeeIds.includes(c.employee.id)) return false;
    if (jobs.length && !jobs.includes(c.jobTitle)) return false;
    if (locations.length && !locations.includes(c.employee.location)) return false;
    if (isCompliant === 'true' && c.gaps.length !== 0) return false;
    if (isCompliant === 'false' && c.gaps.length === 0) return false;
    if (expiryFrom || expiryTo) {
      const inRange = c.employee.attributes.some(
        (a) =>
          a.expiresOn &&
          (!expiryFrom || a.expiresOn >= expiryFrom) &&
          (!expiryTo || a.expiresOn <= expiryTo)
      );
      if (!inRange) return false;
    }
    return true;
  });
}

// The People + Training lists are sortingMode='server', so honour orderBy ("<field> <asc|desc>").
// `accessors` maps the grid column field to a comparable value; unknown fields use `fallback`.
function applyOrderBy<T>(
  rows: T[],
  orderBy: string | null,
  accessors: Record<string, (r: T) => string | number>,
  fallback: (r: T) => string | number
): T[] {
  if (!orderBy) return rows;
  const [field, dir] = orderBy.trim().split(/\s+/);
  const get = accessors[field] ?? fallback;
  const sign = dir === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => {
    const av = get(a);
    const bv = get(b);
    return (av < bv ? -1 : av > bv ? 1 : 0) * sign;
  });
}

// --- People tab: /Teams/summary, /Teams/employees -------------------------------------

// EmployeeSummaryDto (KlayoLib/Requests/Employees/EmployeeSummaryDto.cs).
function toEmployeeSummaryDto(c: Coverage) {
  const { employee: e } = c;
  return {
    employeeId: e.id,
    isOrganizationAdmin: false,
    firstName: e.firstName,
    preferredName: null,
    email: null,
    lastName: e.lastName,
    jobTitle: c.jobTitle,
    hasJob: c.hasJob,
    fullName: `${e.firstName} ${e.lastName}`,
    employeeAttributesCount: e.attributes.length,
    jobAttributesCount: c.mustHave.length,
    gapAttributesCount: c.gaps.length,
    hasProfilePicture: false, // never request a real avatar (fail-closed)
    isCompliant: c.gaps.length === 0,
    totalNumberOfMandatoryAndRequiredAttributesForJob: c.mustHave.length,
    numberOfMetMandatoryAndRequiredAttributes: c.mustHave.length - c.gaps.length,
    location: e.location,
  };
}

function buildTeamSummary(dataset: PreviewDataset, params: URLSearchParams) {
  const rows = filterPopulation(coverages(dataset), params);
  const sum = (f: (c: Coverage) => number) => rows.reduce((s, r) => s + f(r), 0);
  return {
    teamSummary: {
      totalNumberOfEmployees: rows.length,
      numberOfNonCompliantEmployees: rows.filter((r) => r.gaps.length > 0).length,
      numberOfJobClassifications: new Set(dataset.jobs.map((j) => j.title)).size,
      // Mandatory / Required / Desirable each have their own progress bar in the overview.
      attributeStatistic: {
        totalNumberOfMandatoryAttributes: sum((c) => c.mandatory.length),
        numberOfMetMandatoryAttributes: sum((c) => metCount(c, c.mandatory)),
        totalNumberOfRequiredAttributes: sum((c) => c.required.length),
        numberOfMetRequiredAttributes: sum((c) => metCount(c, c.required)),
        totalNumberOfDesirableAttributes: sum((c) => c.desirable.length),
        numberOfMetDesirableAttributes: sum((c) => metCount(c, c.desirable)),
      },
    },
    isInstructor: false,
  };
}

function buildTeamEmployees(dataset: PreviewDataset, params: URLSearchParams) {
  let rows = filterPopulation(coverages(dataset), params);
  // For the People list, AttributeDefinitionIds means "people who hold these competencies".
  const attributeIds = params.getAll('AttributeDefinitionIds');
  if (attributeIds.length) rows = rows.filter((c) => attributeIds.some((id) => c.heldIds.has(id)));
  const all = applyOrderBy(
    rows.map(toEmployeeSummaryDto),
    params.get('orderBy'),
    {
      firstName: (r) => r.fullName.toLowerCase(),
      isCompliant: (r) => (r.isCompliant ? 1 : 0),
      jobAttributesCount: (r) => r.jobAttributesCount,
      gapAttributesCount: (r) => r.gapAttributesCount,
    },
    (r) => r.fullName.toLowerCase()
  );
  const pageSize = Number(params.get('pageSize')) || all.length || 1;
  const currentPage = Number(params.get('pageNumber')) || 1;
  const start = (currentPage - 1) * pageSize;
  const totalPages = Math.max(1, Math.ceil(all.length / pageSize));
  return {
    employees: {
      currentPage,
      totalPages,
      pageSize,
      totalCount: all.length,
      data: all.slice(start, start + pageSize),
      hasPrevious: currentPage > 1,
      hasNext: currentPage < totalPages,
    },
  };
}

// --- Shared short-summary (GetFilterOptions + Competencies met/gap lists) --------------

// EmployeeShortSummaryDto (KlayoLib/Requests/Employees/EmployeeShortSummaryDto.cs).
function toShortSummary(c: Coverage) {
  const { employee: e } = c;
  return {
    employeeId: e.id,
    hasProfilePicture: false,
    firstName: e.firstName,
    middleName: null,
    lastName: e.lastName,
    preferredName: null,
    fullName: `${e.firstName} ${e.lastName}`,
    role: c.jobTitle || null,
    status: ACTIVE,
    isFullAccess: true,
    hasJob: c.hasJob,
    email: '',
    createdDate: null,
    isInstructor: false,
    isTrainingManager: false,
    trainingManagerConfigurationTypeId: null,
    trainingManagerConfigurationTypeName: null,
    location: e.location,
    isCompliant: c.gaps.length === 0,
  };
}

// EmployeeAttributeShortSummaryDto = short summary + proficiency/expiry for the held attribute.
function toAttributeShortSummary(c: Coverage, attributeDefinitionId: string) {
  const held = c.employee.attributes.find((a) => a.attributeDefinitionId === attributeDefinitionId);
  return {
    ...toShortSummary(c),
    proficiencyValue: held ? PROFICIENCY_VALUE[held.proficiency] : null,
    expiryDate: held?.expiresOn ?? null,
  };
}

// --- Competencies (reused by Competencies tab + Tasks + Training) ---------------------

const holdsAll = (c: Coverage, ids: string[]) => ids.every((id) => c.heldIds.has(id));
const jobNeedsAny = (c: Coverage, ids: string[]) => ids.some((id) => c.mustHave.includes(id));

// Has this employee completed a given training course? Explicit completion (completedTrainingIds)
// wins when the dataset states it (e.g. my-team, where completion is a real fact and there is no
// competency framework); otherwise fall back to the synthetic fixtures' competency-derived rule
// (holding every competency the course delivers ⇒ completed).
const hasCompleted = (c: Coverage, tr: { id: string; competencyIds: string[] }) =>
  c.employee.completedTrainingIds
    ? c.employee.completedTrainingIds.includes(tr.id)
    : holdsAll(c, tr.competencyIds);

// One competency's met/gap breakdown — the shared shape used by /Teams/competencies and by the
// `competencies[]` arrays inside tasks and trainings (carries both *list* and *count* fields).
function buildCompetencyDto(rows: Coverage[], def: PreviewDataset['attributeDefinitions'][number]) {
  const met = rows.filter((c) => c.heldIds.has(def.id));
  const gap = rows.filter((c) => c.mustHave.includes(def.id) && !c.heldIds.has(def.id));
  const profs = met
    .map((c) => c.employee.attributes.find((a) => a.attributeDefinitionId === def.id))
    .map((a) => (a ? PROFICIENCY_VALUE[a.proficiency] : 0));
  const averageProficiency = profs.length ? profs.reduce((s, p) => s + p, 0) / profs.length : 0;
  return {
    attributeDefinitionId: def.id,
    attributeName: def.name,
    attributeCategoryNames: [def.category],
    averageProficiency: Math.round(averageProficiency * 100) / 100,
    metEmployees: met.map((c) => toAttributeShortSummary(c, def.id)),
    gapEmployees: gap.map((c) => toShortSummary(c)),
    totalEmployees: met.length + gap.length,
    metEmployeesCount: met.length,
    gapEmployeesCount: gap.length,
    totalEmployeesCount: met.length + gap.length,
  };
}

function buildTeamCompetencies(dataset: PreviewDataset, params: URLSearchParams) {
  const rows = filterPopulation(coverages(dataset), params);
  // On the Competencies tab, AttributeDefinitionIds narrows WHICH competency cards show (not the
  // population — narrowing that would wrongly empty each card's gap list).
  const attributeIds = new Set(params.getAll('AttributeDefinitionIds'));
  const defs = attributeIds.size
    ? dataset.attributeDefinitions.filter((d) => attributeIds.has(d.id))
    : dataset.attributeDefinitions;
  return { competencies: defs.map((def) => buildCompetencyDto(rows, def)) };
}

// --- Tasks tab: /Teams/tasks (drill-in is client-side, no fetch) ----------------------

function buildTeamTasks(dataset: PreviewDataset, params: URLSearchParams) {
  const rows = filterPopulation(coverages(dataset), params);
  const defs = new Map(dataset.attributeDefinitions.map((d) => [d.id, d]));
  const teamRegulationTaskSummary = (dataset.tasks ?? []).map((task) => {
    const met = rows.filter((c) => holdsAll(c, task.competencyIds));
    const gap = rows.filter(
      (c) => !holdsAll(c, task.competencyIds) && jobNeedsAny(c, task.competencyIds)
    );
    return {
      id: task.id,
      name: task.name,
      numberOfCompetencies: task.competencyIds.length,
      numberOfJobDefinitions: new Set(
        rows.filter((c) => jobNeedsAny(c, task.competencyIds)).map((c) => c.jobTitle)
      ).size,
      competencies: task.competencyIds
        .map((id) => defs.get(id))
        .filter((d): d is NonNullable<typeof d> => !!d)
        .map((d) => buildCompetencyDto(rows, d)),
      metEmployees: met.map((c) => toShortSummary(c)),
      gapEmployees: gap.map((c) => toShortSummary(c)),
      employees: [...met, ...gap].map((c) => toShortSummary(c)),
      totalEmployees: met.length + gap.length,
    };
  });
  return { teamRegulationTaskSummary };
}

// --- Training tab: /Teams/Trainings (paged) + /Teams/Trainings/summary ----------------

function trainingDto(
  dataset: PreviewDataset,
  rows: Coverage[],
  tr: NonNullable<PreviewDataset['trainings']>[number]
) {
  const defs = new Map(dataset.attributeDefinitions.map((d) => [d.id, d]));
  const completed = rows.filter((c) => hasCompleted(c, tr));
  const gap = rows.filter((c) => !hasCompleted(c, tr) && jobNeedsAny(c, tr.competencyIds));
  return {
    id: tr.id,
    integrationCourseId: tr.id,
    name: tr.name,
    courseId: tr.id,
    integrationName: 'LearnUpon',
    integrationAbbreviation: tr.integrationAbbreviation,
    competencies: tr.competencyIds
      .map((id) => defs.get(id))
      .filter((d): d is NonNullable<typeof d> => !!d)
      .map((d) => buildCompetencyDto(rows, d)),
    // The list/summary carry COUNTS only; the people-list dialog fetches employees via the
    // /Teams/trainings/:id/(completed|gap|all) drill-in.
    completedEmployees: [],
    gapEmployees: [],
    completedEmployeesCount: completed.length,
    gapEmployeesCount: gap.length,
    competenciesCount: tr.competencyIds.length,
    totalEmployees: completed.length + gap.length,
  };
}

function buildTeamTrainings(dataset: PreviewDataset, params: URLSearchParams) {
  const rows = filterPopulation(coverages(dataset), params);
  let all = (dataset.trainings ?? []).map((tr) => trainingDto(dataset, rows, tr));

  // Training-tab controls are also server-side: the search box (searchText), the "Show gaps only"
  // toggle (showGapsOnly) and the sortable columns (orderBy) all post to /Teams/Trainings.
  const searchText = params.get('searchText')?.trim().toLowerCase();
  if (searchText) all = all.filter((t) => t.name.toLowerCase().includes(searchText));
  if (params.get('showGapsOnly') === 'true') all = all.filter((t) => t.gapEmployeesCount > 0);
  all = applyOrderBy(
    all,
    params.get('orderBy'),
    {
      courseName: (t) => t.name.toLowerCase(),
      competenciesCount: (t) => t.competenciesCount,
      totalEmployees: (t) => t.totalEmployees,
      completedEmployeesCount: (t) => t.completedEmployeesCount,
      gapEmployeesCount: (t) => t.gapEmployeesCount,
    },
    (t) => t.name.toLowerCase()
  );

  const pageSize = Number(params.get('pageSize')) || all.length || 1;
  const currentPage = Number(params.get('pageNumber')) || 1;
  const start = (currentPage - 1) * pageSize;
  const totalPages = Math.max(1, Math.ceil(all.length / pageSize));
  return {
    trainings: {
      currentPage,
      totalPages,
      pageSize,
      totalCount: all.length,
      data: all.slice(start, start + pageSize),
      hasPrevious: currentPage > 1,
      hasNext: currentPage < totalPages,
    },
  };
}

function buildTeamTrainingsSummary(dataset: PreviewDataset, params: URLSearchParams) {
  const rows = filterPopulation(coverages(dataset), params);
  return { trainings: (dataset.trainings ?? []).map((tr) => trainingDto(dataset, rows, tr)) };
}

// Training people-list drill-in (TrainingPeopleListDialog fetches on open — LOWERCASE `trainings`).
// status: 'completed' | 'gap' | 'all'; competencyId narrows to one competency within the course.
function buildTrainingDrillIn(
  dataset: PreviewDataset,
  trainingId: string,
  status: string,
  competencyId?: string
) {
  const rows = coverages(dataset);
  const tr = (dataset.trainings ?? []).find((t) => t.id === trainingId);
  const ids = competencyId ? [competencyId] : (tr?.competencyIds ?? []);
  // Narrowed to one competency ⇒ competency-derived; otherwise honour explicit course completion.
  const isCompleted = (c: Coverage) =>
    competencyId ? holdsAll(c, ids) : tr ? hasCompleted(c, tr) : holdsAll(c, ids);
  const completed = rows.filter(isCompleted);
  const gap = rows.filter((c) => !isCompleted(c) && jobNeedsAny(c, ids));
  const list = status === 'gap' ? gap : status === 'all' ? [...completed, ...gap] : completed;
  return { employees: list.map((c) => toShortSummary(c)) };
}

// --- Profile drill-in: GET /Teams/employees/:id ---------------------------------------

function buildTeamEmployee(dataset: PreviewDataset, employeeId: string) {
  const row = coverages(dataset).find((c) => c.employee.id === employeeId);
  return { employee: row ? toEmployeeSummaryDto(row) : null };
}

// --- My Groups: /MyGroup/:groupId/* mirror the /Teams/* twins over a member subset -----

// Narrows the dataset to a group's members; every group handler reuses the Team builders over it.
function groupDataset(dataset: PreviewDataset, groupId: string): PreviewDataset {
  const group = (dataset.groups ?? []).find((g) => g.groupId === groupId);
  if (!group) return dataset; // fallback: whole company
  const members = new Set(group.memberEmployeeIds);
  return { ...dataset, employees: dataset.employees.filter((e) => members.has(e.id)) };
}

// --- Support calls (fired on Team mount) ----------------------------------------------

function buildFilterOptions(dataset: PreviewDataset) {
  const rows = coverages(dataset);
  return {
    employees: rows.map((c) => toShortSummary(c)),
    jobs: Array.from(new Set(dataset.jobs.map((j) => j.title))),
    locations: Array.from(new Set(dataset.employees.map((e) => e.location))),
  };
}

// AttributeDefinitionDto (KlayoLib/Requests/AttributeDefinitions/AttributeDefinitionDto.cs).
function buildAttributeDefinitions(dataset: PreviewDataset) {
  const attributeDefinitions = dataset.attributeDefinitions.map((def) => ({
    attributeDefinitionId: def.id,
    organizationId: 'preview-org',
    name: def.name,
    description: null,
    code: null,
    attributeCategoryName: def.category,
    hasExpiryDate: dataset.employees.some((e) =>
      e.attributes.some((a) => a.attributeDefinitionId === def.id && a.expiresOn)
    ),
    hasProficiency: true,
    isParent: false,
    hasLinkedPractical: false,
    attributeCategories: null,
    attributeNotifications: null,
    attributeDefinitionChildren: null,
    trainingCourses: [],
  }));
  return { attributeDefinitions };
}

export const teamRoutes: MockRoute[] = [
  {
    pattern: '/Teams/summary',
    build: ({ dataset, url }) => buildTeamSummary(dataset, url.searchParams),
  },
  {
    pattern: '/Teams/employees',
    build: ({ dataset, url }) => buildTeamEmployees(dataset, url.searchParams),
  },
  // Profile drill-in (3 segs — distinct from the 2-seg list above). /Profile lives in profile.ts.
  {
    pattern: '/Teams/employees/:id',
    build: ({ dataset, params }) => buildTeamEmployee(dataset, params.id),
  },
  {
    pattern: '/Teams/competencies',
    build: ({ dataset, url }) => buildTeamCompetencies(dataset, url.searchParams),
  },
  { pattern: '/Teams/InstructorStatus', build: () => ({ isInstructor: false }) },
  { pattern: '/Teams/GetFilterOptions', build: ({ dataset }) => buildFilterOptions(dataset) },
  { pattern: '/AttributeDefinition', build: ({ dataset }) => buildAttributeDefinitions(dataset) },
  // Tasks tab (drill-in is client-side).
  {
    pattern: '/Teams/tasks',
    build: ({ dataset, url }) => buildTeamTasks(dataset, url.searchParams),
  },
  // Training tab — list + summary are CAPITAL `Trainings`; drill-ins are LOWERCASE `trainings`
  // (matchPath is case-sensitive; the two casings are the app's, not ours).
  {
    pattern: '/Teams/Trainings',
    build: ({ dataset, url }) => buildTeamTrainings(dataset, url.searchParams),
  },
  {
    pattern: '/Teams/Trainings/summary',
    build: ({ dataset, url }) => buildTeamTrainingsSummary(dataset, url.searchParams),
  },
  {
    pattern: '/Teams/trainings/:id/:status',
    build: ({ dataset, params }) => buildTrainingDrillIn(dataset, params.id, params.status),
  },
  {
    pattern: '/Teams/trainings/:id/competency/:cid/:status',
    build: ({ dataset, params }) =>
      buildTrainingDrillIn(dataset, params.id, params.status, params.cid),
  },

  // --- My Groups: the same handlers scoped to a group's members (groupDataset). Casing matches
  //     the /Teams twins: list/summary capital `Trainings`, drill-ins lowercase `trainings`.
  {
    pattern: '/MyGroup/:groupId/summary',
    build: ({ dataset, params, url }) =>
      buildTeamSummary(groupDataset(dataset, params.groupId), url.searchParams),
  },
  {
    pattern: '/MyGroup/:groupId/employees',
    build: ({ dataset, params, url }) =>
      buildTeamEmployees(groupDataset(dataset, params.groupId), url.searchParams),
  },
  {
    pattern: '/MyGroup/:groupId/employees/:id',
    build: ({ dataset, params }) =>
      buildTeamEmployee(groupDataset(dataset, params.groupId), params.id),
  },
  {
    pattern: '/MyGroup/:groupId/competencies',
    build: ({ dataset, params, url }) =>
      buildTeamCompetencies(groupDataset(dataset, params.groupId), url.searchParams),
  },
  { pattern: '/MyGroup/:groupId/InstructorStatus', build: () => ({ isInstructor: false }) },
  {
    pattern: '/MyGroup/:groupId/GetFilterOptions',
    build: ({ dataset, params }) => buildFilterOptions(groupDataset(dataset, params.groupId)),
  },
  {
    pattern: '/MyGroup/:groupId/tasks',
    build: ({ dataset, params, url }) =>
      buildTeamTasks(groupDataset(dataset, params.groupId), url.searchParams),
  },
  {
    pattern: '/MyGroup/:groupId/Trainings',
    build: ({ dataset, params, url }) =>
      buildTeamTrainings(groupDataset(dataset, params.groupId), url.searchParams),
  },
  {
    pattern: '/MyGroup/:groupId/Trainings/summary',
    build: ({ dataset, params, url }) =>
      buildTeamTrainingsSummary(groupDataset(dataset, params.groupId), url.searchParams),
  },
  {
    pattern: '/MyGroup/:groupId/trainings/:id/:status',
    build: ({ dataset, params }) =>
      buildTrainingDrillIn(groupDataset(dataset, params.groupId), params.id, params.status),
  },
  {
    pattern: '/MyGroup/:groupId/trainings/:id/competency/:cid/:status',
    build: ({ dataset, params }) =>
      buildTrainingDrillIn(
        groupDataset(dataset, params.groupId),
        params.id,
        params.status,
        params.cid
      ),
  },
];
